import { Fragment, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { WarningIcon } from '@phosphor-icons/react';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SystemEntityInline } from '@/components/system-entities';
import { formatCurrency } from '@/utils/formatters';
import { cn } from '@/lib/utils';
import { SplitProposalCards } from './SplitProposalCards';
import {
  buildSplitProposalCards,
  type SplitProposalLineInput,
} from './splitProposalCardData';
import { useRelatedLinesSummary } from './useRelatedLinesSummary';
import { useSplitProposalAnswers } from './useSplitProposalAnswers';
import {
  hasDifference,
  type InactiveReason,
  type RelatedDocumentGroup,
  type RelatedLineRow,
  type RelatedLinesSummary,
} from './relatedLinesSummary';

type FormatMoney = (amount: string | null) => string;

function useRelatedLineLabels() {
  const gt = useGT();
  return {
    rowLabel: (row: RelatedLineRow) =>
      row.label ??
      (row.kind === 'charge'
        ? gt('Charge', { context: 'fallback name for an unnamed charge' })
        : gt('Line item', { context: 'fallback name for an unnamed PO line' })),
    inactiveReasonLabel: (reason: InactiveReason) => {
      switch (reason) {
        case 'reversal':
          return gt('Reversed', {
            context:
              'tag on a Famous charge row that cancels an earlier charge',
          });
        case 'superseded':
          return gt('Replaced', {
            context:
              'tag on a Famous charge row replaced by a corrected charge',
          });
        case 'inactive':
          return gt('Not counted', {
            context:
              'tag on a charge that does not count toward the document total',
          });
      }
    },
  };
}

function MatchedToCell({ row }: { row: RelatedLineRow }) {
  const { inactiveReasonLabel } = useRelatedLineLabels();
  if (row.inactiveReason) {
    return (
      <Badge variant="outline">{inactiveReasonLabel(row.inactiveReason)}</Badge>
    );
  }
  if (
    row.matchedInvoiceLineNumbers.length === 0 &&
    row.proposedInvoiceLineNumbers.length > 0
  ) {
    return (
      <span className="flex flex-wrap items-center gap-2 text-sm">
        <Badge
          variant="outline"
          className="border-sky-300 text-sky-700 dark:text-sky-300"
        >
          <T context="status of a related line a suggested split would match, not yet accepted">
            Proposed
          </T>
        </Badge>
        <T context="related line would be matched to these invoice line numbers if the suggested split is accepted">
          Invoice line <Var>{row.proposedInvoiceLineNumbers.join(', ')}</Var>
        </T>
      </span>
    );
  }
  if (row.matchedInvoiceLineNumbers.length === 0) {
    return (
      <Badge
        variant="outline"
        className="border-amber-300 text-amber-700 dark:text-amber-300"
      >
        <T>Unmatched</T>
      </Badge>
    );
  }
  return (
    <span className="text-sm">
      <T context="related line is matched to these invoice line numbers">
        Invoice line <Var>{row.matchedInvoiceLineNumbers.join(', ')}</Var>
      </T>
    </span>
  );
}

function RelatedLineTableRow({
  row,
  money,
}: {
  row: RelatedLineRow;
  money: FormatMoney;
}) {
  const { rowLabel } = useRelatedLineLabels();
  const inactive = row.inactiveReason !== null;
  return (
    <TableRow className={cn(inactive && 'text-muted-foreground')}>
      <TableCell className="pl-8">
        <div className={cn('text-sm', inactive && 'line-through')}>
          {rowLabel(row)}
        </div>
        {row.detail && (
          <div className="text-xs text-muted-foreground">{row.detail}</div>
        )}
      </TableCell>
      <TableCell
        className={cn('text-right tabular-nums', inactive && 'line-through')}
      >
        {money(row.amount)}
      </TableCell>
      <TableCell>
        <MatchedToCell row={row} />
      </TableCell>
    </TableRow>
  );
}

function RelatedDocumentGroupRows({
  group,
  money,
}: {
  group: RelatedDocumentGroup;
  money: FormatMoney;
}) {
  return (
    <Fragment>
      <TableRow className="bg-muted/40 hover:bg-muted/40">
        <TableCell>
          <SystemEntityInline
            entityType={group.entityType}
            entityId={group.entityId}
          />
        </TableCell>
        <TableCell className="text-right font-medium tabular-nums">
          {money(group.subtotal)}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {group.rows.length === 0 && <T>No active lines</T>}
        </TableCell>
      </TableRow>
      {group.rows.map((row) => (
        <RelatedLineTableRow key={row.key} row={row} money={money} />
      ))}
    </Fragment>
  );
}

function RelatedLinesFooter({
  summary,
  money,
}: {
  summary: RelatedLinesSummary;
  money: FormatMoney;
}) {
  return (
    <TableFooter>
      <TableRow>
        <TableCell>
          <T>Related lines total</T>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {money(summary.relatedTotal)}
        </TableCell>
        <TableCell />
      </TableRow>
      <TableRow>
        <TableCell>
          <T>Matched total</T>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {money(summary.matchedTotal)}
        </TableCell>
        <TableCell />
      </TableRow>
      {summary.proposedCount > 0 && (
        <TableRow className="text-sky-700 dark:text-sky-300">
          <TableCell>
            <T context="total of related lines a suggested split would match, not yet accepted">
              Proposed total (not accepted yet)
            </T>
          </TableCell>
          <TableCell className="text-right tabular-nums">
            {money(summary.proposedTotal)}
          </TableCell>
          <TableCell />
        </TableRow>
      )}
      <TableRow>
        <TableCell>
          <T>Invoice total</T>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {money(summary.invoiceTotal)}
        </TableCell>
        <TableCell />
      </TableRow>
      <TableRow className={cn(hasDifference(summary) && 'text-red-600')}>
        <TableCell>
          <T context="invoice total minus the total of all related load/order/PO lines">
            Difference
          </T>
        </TableCell>
        <TableCell className="text-right font-medium tabular-nums">
          {money(summary.difference)}
        </TableCell>
        <TableCell />
      </TableRow>
    </TableFooter>
  );
}

interface RelatedDocumentLinesProps {
  invoiceId: string;
  lineItems: SplitProposalLineInput[];
  /** The invoice is still draft or matched, so a suggestion can be answered. */
  canEditMatches?: boolean;
  invoiceTotal: string | null;
  invoiceSupplierId?: string | null;
  currency?: string | null;
}

/**
 * "Related lines" tab: every active line on the load, order, transfer and PO
 * the invoice is matched against, grouped by document, with the invoice line
 * each one is matched to. Lets AP spot related lines the invoice does not
 * cover, like the Famous Purchase Matching screen. When one invoice line bills
 * several of them together, the tab shows the suggested split and lets AP
 * accept or dismiss it.
 */
export function RelatedDocumentLines({
  invoiceId,
  lineItems,
  invoiceTotal,
  invoiceSupplierId,
  currency,
  canEditMatches = true,
}: RelatedDocumentLinesProps) {
  const answers = useSplitProposalAnswers({ invoiceId, canEditMatches });
  const [showHistory, setShowHistory] = useState(false);
  const { summary, hasDocuments, isLoading } = useRelatedLinesSummary({
    invoiceId,
    lineItems,
    invoiceTotal,
    invoiceSupplierId,
    showHistory,
  });
  const money: FormatMoney = (amount) =>
    amount === null
      ? '—'
      : formatCurrency(Number(amount), { currency: currency || 'USD' });

  const proposalCards = buildSplitProposalCards(lineItems);

  if (isLoading) {
    return (
      <div className="px-3 py-4 text-sm text-muted-foreground">
        <T>Loading related lines...</T>
      </div>
    );
  }

  if (!hasDocuments) {
    return (
      <div className="px-3 py-4 text-sm text-muted-foreground">
        <T context="AP invoice tab listing lines from the load, order and PO the invoice is matched to">
          This invoice is not linked to any load, order or purchase order yet.
        </T>
      </div>
    );
  }

  return (
    <div className="px-3 space-y-2">
      <div className="flex items-center justify-end gap-2">
        <Switch
          id="show-related-line-history"
          checked={showHistory}
          onCheckedChange={setShowHistory}
        />
        <Label
          htmlFor="show-related-line-history"
          className="text-xs text-muted-foreground font-normal cursor-pointer"
        >
          <T context="toggle on the AP invoice related-lines tab; reveals Famous charge rows that were reversed or replaced and do not count toward totals">
            Show reversed and replaced lines
          </T>
        </Label>
      </div>
      <SplitProposalCards
        cards={proposalCards}
        money={money}
        disabledReason={answers.disabledReason}
        pendingLineId={answers.pendingLineId}
        onAccept={answers.accept}
        onDismiss={answers.dismiss}
      />
      {summary.unmatchedCount > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <WarningIcon className="h-4 w-4 shrink-0" />
          <T context="AP invoice related-lines tab; lines on the linked load/order/PO that no invoice line is matched to">
            <Var>{summary.unmatchedCount}</Var> related lines are not matched to
            any invoice line.
          </T>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <T>Line</T>
            </TableHead>
            <TableHead className="text-right">
              <T>Amount</T>
            </TableHead>
            <TableHead>
              <T context="column showing which invoice line a related load/order/PO line is matched to">
                Matched to
              </T>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {summary.groups.map((group) => (
            <RelatedDocumentGroupRows
              key={`${group.entityType}:${group.entityId}`}
              group={group}
              money={money}
            />
          ))}
        </TableBody>
        <RelatedLinesFooter summary={summary} money={money} />
      </Table>
    </div>
  );
}
